import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as API from '../../lib/api';
import styled from 'styled-components';

import { Helmet } from 'react-helmet-async';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNewspaper
} from '@fortawesome/free-solid-svg-icons';

import SpinnerComponent from '../Common/Spinners';

import LeftSidebar from '../Common/LeftSidebar';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: .85em;
  min-width: 20em;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    font-size: .85em;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2em;
  max-width: 45em;
  
  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
  }
`

const PageTitle = styled.div`
  font-size: 2em;
  font-weight: bold;
`

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const SearchForm = styled.form`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 1em;
  padding: .5em;
  border: 1px dashed #333;
`

const SearchInput = styled.input`
  margin-right: 1em;
  height: 2em;
`

const SearchButton = styled.button`
  padding: .1em 1em;
  height: 2em;
  max-width: 6em;
  border: 1px solid #ccc;
  border-radius: 2px;
  text-align: center;
  font-size: 1em;
  cursor: pointer;

  &:hover {
    background: #228fd8;
    color: white;
  }
`

const ErrorMessage = styled.div`
  margin: .5em 0;
  padding: 1em;
  background: #F5BCBC;
  color: #600;
  text-align: center;
  ${props => props.$activeError ? 'display: flex;' : 'display: none;'}
`

const ResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1em;
  border-bottom: 1px solid black;
`

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-top: 1px solid black;
  border-bottom: 1px solid black;
  padding-right: 17px;
  font-weight: bold;
`

const HeaderItem = styled.div`
  flex: 1;
`

const PlayerRowsWrapper = styled.div`
  max-height: 30em;
  overflow-y: scroll;
`

const PlayerRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: .2em 0;
  background-color: ${props => props.$hovered ? '#bbb' : props.$rowIndex % 2 > 0 ? '#ddd' : 'white'};
`

const PlayerCell = styled.div`
  flex: 1;

  > a {
    font-weight: bold;
    color: black;
  }

  > a > svg:hover {
    color: #228fd8 !important;
  }
`

const PlayerRow = (props) => {
  const [hovered, setHovered] = useState(false);

  return (
    <PlayerRowContainer
      $hovered={hovered}
      $rowIndex={props.rowIndex}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <PlayerCell>
        <Link to={`/player/${props.player.playerId}`}>
          <FontAwesomeIcon
            icon={faNewspaper}
            title="View player's profile page"
          />
        </Link>
        &nbsp;
        <Link
          to={`/player/${props.player.playerId}`}
          title="View player's profile page"
        >
          {props.player.nickname}
        </Link>
      </PlayerCell>
      <PlayerCell>
        {props.player.firstName} {props.player.lastName}
      </PlayerCell>
      <PlayerCell>
        {props.player.teamName || '--'}
      </PlayerCell>
    </PlayerRowContainer>
  )
}

const PlayerProfile = ({ AppStore }) => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const [searchInput, setSearchInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const route = 'players';
  const didRouteChange = route !== AppStore.currentRoute.get();

  const playersInSearchResult = AppStore.playerSearchResults;

  const pageTitle = 'Oracle\'s Elixir Valorant - Players';
  const pageDescription = 'Find players (WIP).';

  const findPlayers = async (searchTerm) => {
    AppStore.setSearchingForPlayers(true);
    try {
      const result = await API.findPlayers(searchTerm);
      if (result.length === 0) {
        setErrorMessage(`No players found with "${searchTerm}" in their nickname or real name`);
      }
      AppStore.playerSearchResults.replace(result);
    } catch (error) {
      console.log(error);
    }
    AppStore.setSearchingForPlayers(false);
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    AppStore.playerSearchResults.replace([]);
    if (searchInput.length === 0) {
      setErrorMessage('');
      AppStore.playerSearchResults.replace([]);
    } else if (searchInput.length < 3) {
      setErrorMessage('Search term must be at least 3 characters long');
    } else {
      setErrorMessage('');
      findPlayers(searchInput);
    }
  }

  useEffect(() => {
    if (didRouteChange) {
      AppStore.setCurrentRoute(route);
      AppStore.playerSearchResults.replace([]);
    }
  }, [
    AppStore,
    didRouteChange,
    playersInSearchResult
  ]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name={pageTitle}
          content={pageDescription}
        />
      </Helmet>
      <LeftSidebar />
      <Wrapper className='mainContent'>
        <Content>
          <PageTitle>Player Search</PageTitle>
          <SearchWrapper>
            <SearchForm>
              <SearchInput
                id='player-search'
                value={searchInput}
                placeholder='Enter 3+ characters'
                onChange={(event) => setSearchInput(event.target.value)}
                autoFocus
              />
              <SearchButton
                onClick={handleSubmit}
                type='submit'
              >
                Search
              </SearchButton>
            </SearchForm>
            <ErrorMessage
              $activeError={errorMessage !== ''}
            >
              {errorMessage}
            </ErrorMessage>
          </SearchWrapper>
          {AppStore.searchingForPlayers.get() ? (
            <SpinnerComponent />
          ) : playersInSearchResult.length > 0 ? (
            <ResultsWrapper>
              <HeaderRow>
                <HeaderItem>Player</HeaderItem>
                <HeaderItem>Name</HeaderItem>
                <HeaderItem>Team</HeaderItem>
              </HeaderRow>
              <PlayerRowsWrapper>
                {playersInSearchResult.map((player, idx) => {
                  return (
                    <PlayerRow
                      key={`${player.playerId}-row`}
                      rowIndex={idx}
                      player={player}
                    />
                  )
                })}
              </PlayerRowsWrapper>
            </ResultsWrapper>
          ) : (
            null
          )}
        </Content>
      </Wrapper>
    </>
  );
};

export default inject("AppStore")(observer(PlayerProfile));
